import type { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { listFormatLocale } from './stationFaqEntries'
import styles from './StationAboutSection.module.css'

export interface AboutLine {
  name: string
  colorHex: string
}

interface Props {
  stationName: string
  /** Fare zone from schema.json ("1", "2/3", …). */
  zone: string | undefined
  /** Street from the station-content address; clause omitted without it. */
  street: string | undefined
  /** Every line serving the station, display order. */
  lines: readonly AboutLine[]
  /** Nearby landmarks derived from the exits (landmarksFromExits). */
  landmarks: readonly string[]
}

/** Placeholder the node-interpolation splits on — never shown to users. */
const NODE_SLOT = '@@NODE@@'

/**
 * Localized "A, B and C" as React nodes: `Intl.ListFormat.formatToParts`
 * supplies the locale's separators as literals while each list element
 * renders through `renderItem` (line names get their line-color span,
 * landmarks their underline span — proper nouns stay English).
 */
function formatListNodes(
  items: readonly string[],
  locale: string,
  renderItem: (item: string) => ReactNode,
): ReactNode[] {
  try {
    return new Intl.ListFormat(listFormatLocale(locale), { type: 'conjunction' })
      .formatToParts([...items])
      .map((part, index) =>
        part.type === 'element' ? (
          <span key={`e${index}`}>{renderItem(part.value)}</span>
        ) : (
          part.value
        ),
      )
  } catch {
    return items.flatMap((item, index) =>
      index === 0 ? [renderItem(item)] : [', ', renderItem(item)],
    )
  }
}

/** Splits a translated sentence on {@link NODE_SLOT} and injects `node`. */
function withNode(sentence: string, node: ReactNode): ReactNode[] {
  const [before, after] = sentence.split(NODE_SLOT)
  return [before, node, after ?? '']
}

/**
 * About — Figma 8597:41868 (TASK-699). The page's SEO copy block:
 * an h2 ("About {station} Underground Station") and two paragraphs
 * assembled from data the page already has — street + zone + served
 * lines (names tinted in their line colours), then nearby landmarks
 * and the static page-purpose sentence. Copy resolves through the
 * `station-page` namespace as full-sentence templates, so every locale
 * gets grammatical prose while the proper nouns stay English.
 * Everything is deterministic so the copy can be prerendered.
 */
export function StationAboutSection({
  stationName,
  zone,
  street,
  lines,
  landmarks,
}: Props): ReactElement {
  const { t, i18n } = useTranslation('station-page')
  const locale = i18n.language

  const central = zone === '1'
  const located =
    street !== undefined
      ? t(central ? 'about_located_street_central' : 'about_located_street', {
          station: stationName,
          street,
        })
      : t(central ? 'about_located_central' : 'about_located', { station: stationName })

  const lineNodes = formatListNodes(
    lines.map(({ name }) => name),
    locale,
    (name) => {
      const line = lines.find((l) => l.name === name)
      return (
        <span className={styles.lineName} style={{ color: line?.colorHex }}>
          {name}
        </span>
      )
    },
  )

  const landmarkNodes = formatListNodes(landmarks, locale, (landmark) => (
    <span className={styles.landmark}>{landmark}</span>
  ))

  const zoneLinesSentence =
    zone !== undefined && lines.length > 0
      ? withNode(t('about_zone_lines', { zone, lines: NODE_SLOT, count: lines.length }), lineNodes)
      : zone !== undefined
        ? [t('about_zone_only', { zone })]
        : lines.length > 0
          ? withNode(t('about_lines_only', { lines: NODE_SLOT, count: lines.length }), lineNodes)
          : []

  return (
    <div className={styles.section}>
      <h2 className={styles.caption}>{t('about_heading', { station: stationName })}</h2>
      <div className={styles.body}>
        <p className={styles.paragraph}>
          {located}
          {zoneLinesSentence.length > 0 && ' '}
          {zoneLinesSentence}
        </p>
        <p className={styles.paragraph}>
          {landmarks.length > 0 && (
            <>{withNode(t('about_landmarks', { landmarks: NODE_SLOT }), landmarkNodes)} </>
          )}
          {t('about_purpose')}
        </p>
      </div>
    </div>
  )
}
